// Thin fetch wrapper for the same-origin API (docs/pipeline/V1-CONTRACT.md).

export class ApiError extends Error {
  status: number;
  code: string;
  field?: string;
  existing_id?: string;
  constructor(status: number, code: string, message: string, field?: string, existing_id?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.field = field;
    this.existing_id = existing_id;
  }
}

const MUTATING = new Set(["POST", "PATCH", "PUT", "DELETE"]);

export async function api<T>(
  path: string,
  init: { method?: string; body?: unknown; signal?: AbortSignal; headers?: Record<string, string> } = {},
): Promise<T> {
  const method = init.method ?? "GET";
  const headers: Record<string, string> = { ...init.headers, Accept: "application/json" };
  if (MUTATING.has(method)) headers["X-Pipeline-Client"] = "web";
  if (init.body !== undefined) headers["Content-Type"] = "application/json";
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      signal: init.signal,
      headers,
      credentials: "same-origin",
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
  } catch {
    throw new ApiError(0, "network", "Can't reach the server. Check your connection.");
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = undefined;
    }
  }
  if (!res.ok) {
    const d = (data ?? {}) as { error?: string; message?: string; field?: string; existing_id?: string };
    throw new ApiError(res.status, d.error ?? `http_${res.status}`, d.message ?? defaultMessage(res.status), d.field, d.existing_id);
  }
  return data as T;
}

function defaultMessage(status: number): string {
  if (status === 401) return "Your session ended. Sign in again.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "Not found.";
  if (status === 409) return "Someone else changed this first.";
  if (status >= 500) return "The server had a problem. Try again.";
  return `Request failed (${status})`;
}

export function isRetryable(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 0 || err.status >= 500);
}

/** The OIDC login URL; without a tenant the server uses the primary (LendSure) tenant. */
export function loginHref(returnTo: string, tenantId?: string): string {
  const q = new URLSearchParams();
  if (tenantId) q.set("tenant", tenantId);
  q.set("return_to", returnTo);
  return `/api/auth/login?${q.toString()}`;
}
